import { useRef, type ReactNode } from 'react';
import type { TileOf } from '@casa/model';
import { drawElements } from '../../elements/layout_elements.ts';
import { lastNumber } from '../../values/readings.ts';
import { CanvasView } from '../../view/canvas_view.tsx';
import { useLegacyIcon } from '../../view/icons.ts';
import { useScreenActive } from '../../view/screen_activity.ts';
import { TILE_FONT } from '../../view/theme.ts';
import { useTileTextColor } from '../../view/tile_style.ts';
import { useTileEnvironment } from '../../view/tile_environment.ts';
import { prefersReducedMotion } from '../../view/tween.ts';
import { drawQuietSolar, quietSolarContent, type QuietFlowStates } from './solar_quiet.ts';
import { solarTileContent, solarTileElements, type SolarFlowStates } from './solar_tile.ts';

/** The options of a `solar` tile that name its sources, in the order of `SolarValues`. */
const SOURCE_OPTIONS = ['load', 'pv', 'batteryCharge', 'grid', 'gridVoltage', 'batterySoc'] as const;

/**
 * The `solar` tile, animated on the shared frame loop while its screen is shown: the quiet cross,
 * with light icons, or the legacy drawing (SO-2). The whole tile is drawn on every frame; the text
 * layout is cached, so a frame is mostly path filling.
 */
export function SolarTileView({ tile }: { tile: TileOf<'solar'> }) : ReactNode {
    const active = useScreenActive();
    const environment = useTileEnvironment();
    const textColor = useTileTextColor('#ffffff');
    const readings = environment.useReadingsList(
        tile,
        SOURCE_OPTIONS.map((key) => tile.options[key]),
    );
    const [load, pv, batteryCharge, grid, gridVoltage, batterySoc] = readings.map(lastNumber);
    const values = {
        load: load ?? null,
        pv: pv ?? null,
        batteryCharge: batteryCharge ?? null,
        grid: grid ?? null,
        gridVoltage: gridVoltage ?? null,
        batterySoc: batterySoc ?? null,
    };
    const isQuiet = tile.options.design !== 'classic';
    const hasBattery = tile.options.batteryCharge !== undefined || tile.options.batterySoc !== undefined;
    const quiet = quietSolarContent(values, hasBattery, environment.locale);
    const content = solarTileContent(values, environment.locale, textColor);
    // Each design loads only its own icons: a name left undefined loads nothing.
    const classicIcons = {
        pv: useLegacyIcon(isQuiet ? undefined : 'solar_panel'),
        home: useLegacyIcon(isQuiet ? undefined : 'house'),
        grid: useLegacyIcon(isQuiet ? undefined : 'utility_pole'),
        battery: useLegacyIcon(isQuiet ? undefined : 'battery_empty'),
    };
    const quietIcons = {
        pv: useLegacyIcon(isQuiet ? 'solar-panel' : undefined, 'light'),
        home: useLegacyIcon(isQuiet ? 'house' : undefined, 'light'),
        grid: useLegacyIcon(isQuiet ? 'utility-pole' : undefined, 'light'),
        battery: useLegacyIcon(isQuiet ? quiet.batteryIcon : undefined, 'light'),
    };
    const icons = isQuiet ? quietIcons : classicIcons;
    const states = useRef<SolarFlowStates>({ pv: { offset: 0 }, home: { offset: 0 }, grid: { offset: 0 }, battery: { offset: 0 } });
    const quietStates = useRef<QuietFlowStates>({ pv: { offset: 0 }, home: { offset: 0 }, grid: { offset: 0 }, battery: { offset: 0 } });

    return (
        <div style={{ position: 'absolute', inset: 0 }}>
            <CanvasView
                // The quiet cross's dots stand still where the system asks for reduced motion (SO-2).
                animate={active && !(isQuiet && prefersReducedMotion())}
                deps={[isQuiet, JSON.stringify(isQuiet ? quiet : content), icons.pv, icons.home, icons.grid, icons.battery, textColor]}
                draw={(ctx, width, height, now) => {
                    const box = { left: 0, top: 0, width, height };

                    if (isQuiet) {
                        drawQuietSolar(ctx, box, quiet, icons, quietStates.current, now, TILE_FONT, textColor);
                    } else {
                        drawElements(ctx, box, solarTileElements(content, icons, states.current, now, TILE_FONT, textColor));
                    }
                }}
            />
        </div>
    );
}
